import { Box } from "@mui/material";
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import { useChatSessionsContext } from "../lib/contexts/ChatSessionsContext";
import { useCustomThemeContext } from "../lib/contexts/ThemeContext";
import { useUserInfoContext } from "../lib/contexts/UserInfoContext";
import { AnswerFormatter } from "../lib/helpers/answerFormatter";
import { useChat } from "../lib/hooks/UseChat";
import AnswerLoadingState from "./answerLoadingState";
import ChatBubble from "./chatBubble";
import InputArea from "./inputArea";
import WelcomeMessage from "./welcomeMessage";
import { Sources } from "../lib/models/Chat";
import { Message } from "../lib/interfaces/Chat";
import ChatFilters from "./chatFilters";

interface MessageWindowProps {
  chatId: string;
  addMessage: (
    currentMessage: string,
    fromUser: boolean,
    exchangeId: string,
    sources: Sources
  ) => void;
  isLoading: MutableRefObject<boolean>;
}

export const MessageWindow = ({
  chatId,
  addMessage,
  isLoading,
}: MessageWindowProps) => {
  const { postMessage } = useChat();
  const {
    chatSessions,
    addNewChatSession,
    updateNewChatSession,
    updateChatSessionTitle,
    selectedChatSession,
    messages,
    setMessages,
    noMsgReset,
    filterFromYear,
    filterToYear,
    filterDocumentTypes,
    filterAuthor,
    beginChatStream,
    isStreamGenerationActive,
  } = useChatSessionsContext();
  const { custom } = useCustomThemeContext();
  const { userInfo } = useUserInfoContext();

  const endOfMessages = useRef<null | HTMLDivElement>(null); // variable that holds the reference to the last message in the chat
  const messagesContainerRef = useRef<null | HTMLDivElement>(null); // ref for scrollable container
  const [autoScrollEnabled, setAutoScrollEnabled] = useState<boolean>(true); // track if auto-scroll should happen
  const prevScrollTopRef = useRef<number>(0); // track previous scrollTop
  const [currentMessage, setCurrentMessage] = useState<string>(""); // handling state of current message being typed by user
  const [isChatFilterExpanded, setIsChatFilterExpanded] = useState(false); // handling state of chat filter section
  const currentStreamingContent = useRef<string>(""); // track current streaming content
  const [isLoadingSources, setIsLoadingSources] = useState<boolean>(false); // track if sources are being loaded
  const [isAnswerComplete, setIsAnswerComplete] = useState<boolean>(false); // track if answer streaming is complete
  const [isWebToolEnabled, setIsWebToolEnabled] = useState<boolean>(false); // track web tool toggle state

  // New Chat clears session selection; reset local stream UI flags so input is not left disabled.
  useEffect(() => {
    if (!selectedChatSession) {
      currentStreamingContent.current = "";
      setIsLoadingSources(false);
      setIsAnswerComplete(false);
    }
  }, [selectedChatSession]);

  //sets the text in the textbox as the current message
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentMessage(event.target.value);
  };

  //handles the click of a filter button
  const handleChatFilterExpandedChange = () => {
    setIsChatFilterExpanded((prev) => !prev);
  };

  //handles the web tool toggle
  const handleWebToolToggle = () => {
    setIsWebToolEnabled((prev) => {
      const newValue = !prev;
      console.log("Web Tool Toggle:", newValue);
      return newValue;
    });
  };

  const handleSubmit = (event?: React.FormEvent, selectedQuestion?: string) => {
    event?.preventDefault();
    currentStreamingContent.current = ""; // Reset streaming content
    setIsLoadingSources(false); // Reset sources loading state
    setIsAnswerComplete(false); // Reset answer complete state
    let newChatId: string | undefined;
    let initialBotMessage = false;

    if (selectedChatSession) {
      if (
        (currentMessage !== "" || selectedQuestion) &&
        messages.length === 0
      ) {
        // update the chat session title if it is a new chat session
        updateNewChatSession(
          selectedQuestion ? selectedQuestion : currentMessage
        );
      }
    } else if (!selectedQuestion && currentMessage) {
      // the user submitted a question from the homepage
      noMsgReset.current = true;
      newChatId = addNewChatSession(currentMessage);
    } else {
      // a prefilled question from homepage was selected
      noMsgReset.current = true;
      newChatId = addNewChatSession(selectedQuestion);
    }

    isLoading.current = true;
    addMessage(
      selectedQuestion ? selectedQuestion : currentMessage,
      true,
      "",
      {}
    );

    if (userInfo) {
      let currentResponse = "";
      let exchangeId = "";
      let sources = {};
      const { generation } = beginChatStream();

      postMessage(
        selectedQuestion ? selectedQuestion : currentMessage,
        userInfo,
        newChatId ? newChatId : chatId,
        (streamedData) => {
          // Background side effect: abandoned streams may still persist AI titles.
          if (streamedData.type === "final_response") {
            const generatedTitle = streamedData.data.generatedTitle;
            const targetChatId = newChatId || selectedChatSession;
            if (generatedTitle && targetChatId) {
              console.log(
                `Received AI-generated title: ${generatedTitle} for chat: ${targetChatId}`
              );
              console.log(
                `newChatId: ${newChatId}, selectedChatSession: ${selectedChatSession}`
              );
              updateChatSessionTitle(targetChatId, generatedTitle);
            }
          }

          // Ignore UI updates from superseded streams (e.g. after New Chat).
          if (!isStreamGenerationActive(generation)) {
            return;
          }
          if (streamedData.type === "answer" && isLoading.current) {
            currentResponse = currentResponse + streamedData.data.content;
            const formattedResponse = AnswerFormatter(currentResponse, {});

            // Update the message directly in the messages array
            if (formattedResponse.trim()) {
              // Update streaming content
              currentStreamingContent.current = formattedResponse;

              // Don't set sources loading here - wait for answer_complete

              // Update messages array
              setMessages((prevMessages: Message[]) => {
                if (!isStreamGenerationActive(generation)) {
                  return prevMessages;
                }
                if (!initialBotMessage) {
                  // Add new bot message if it doesn't exist
                  initialBotMessage = true;
                  return [
                    ...prevMessages,
                    {
                      message: formattedResponse,
                      fromUser: false,
                      exchangeId: "",
                      sources: {},
                    },
                  ];
                } else {
                  // Update existing bot message with new content
                  const updatedMessages = [...prevMessages];
                  const lastMessage =
                    updatedMessages[updatedMessages.length - 1];
                  if (lastMessage && !lastMessage.fromUser) {
                    lastMessage.message = formattedResponse;
                  }
                  return updatedMessages;
                }
              });
            }
          } else if (
            streamedData.type === "answer_complete" &&
            isLoading.current
          ) {
            // Answer streaming is complete, now waiting for sources
            // console.log('Answer streaming complete, starting to load sources');
            setIsAnswerComplete(true);
            setIsLoadingSources(true);
            isLoading.current = false;
          } else if (streamedData.type === "sources") {
            // Sources can arrive as a separate event before final_response.
            // If we already finished the answer, stop showing "Loading sources..." as soon as sources arrive.
            const incomingSources: any =
              (streamedData as any)?.data?.sources ?? (streamedData as any)?.data ?? {};
            sources = incomingSources || {};

            if (isAnswerComplete) {
              setIsLoadingSources(false);
            }

            // Update last bot message sources immediately so the accordion can render without waiting for final_response.
            setMessages((prevMessages: Message[]) => {
              if (!isStreamGenerationActive(generation)) {
                return prevMessages;
              }
              const updatedMessages = [...prevMessages];
              const lastMessage = updatedMessages[updatedMessages.length - 1];
              if (lastMessage && !lastMessage.fromUser) {
                updatedMessages[updatedMessages.length - 1] = {
                  ...lastMessage,
                  sources: sources,
                };
              }
              return updatedMessages;
            });
          } else if (streamedData.type === "final_response") {
            exchangeId = streamedData.data.exchangeId || "";
            sources = streamedData.data.sources || {};

            // Clear streaming content and update with final content and sources
            currentStreamingContent.current = "";
            // console.log('Sources arrived, setting loading to false. Sources:', sources);
            setIsLoadingSources(false); // Sources have arrived
            setIsAnswerComplete(false); // Stop showing "Loading sources..." for empty sources cases too
            const formattedResponse = AnswerFormatter(currentResponse, sources);
            // console.log('Final formatted response:', formattedResponse);

            setMessages((prevMessages: Message[]) => {
              if (!isStreamGenerationActive(generation)) {
                return prevMessages;
              }
              const updatedMessages = [...prevMessages];
              const lastMessage = updatedMessages[updatedMessages.length - 1];
              // Guard: after New Chat, messages may be empty — do not crash on undefined lastMessage.
              if (lastMessage && !lastMessage.fromUser) {
                updatedMessages[updatedMessages.length - 1] = {
                  message: formattedResponse,
                  fromUser: false,
                  exchangeId: exchangeId,
                  sources: sources,
                };
              }
              return updatedMessages;
            });
          }
        },
        () => {
          // Complete handler — only clear loading for the active stream generation.
          if (!isStreamGenerationActive(generation)) {
            return;
          }
          isLoading.current = false;
          setIsLoadingSources(false);
          setIsAnswerComplete(false);
        },
        filterFromYear,
        filterToYear,
        filterDocumentTypes,
        filterAuthor,
        isWebToolEnabled
      );
    }
    setCurrentMessage(""); // resetting currentMessage variable so that it can then hold the new message entered by the user
  };

  // Scroll to bottom when messages change, only if autoScrollEnabled
  useEffect(() => {
    if (autoScrollEnabled && endOfMessages.current) {
      endOfMessages.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, autoScrollEnabled]);

  return (
    <Box
      sx={{
        background: custom.palette.primary.main,
        paddingLeft: "3%",
        paddingRight: "3%",
        display: "flex",
        paddingBottom: "1%",
        flexDirection: "column",
        width: { xs: "-webkit-fill-available", lg: "75%", xl: "80%" },
      }}
    >
      {/* if the array of messages is empty (if no messages have been sent yet), the phone image and intro text will be rendered */}
      {chatSessions.length === 0 ||
      (messages.length === 0 && !isLoading.current) ? (
        <WelcomeMessage
          chatId={chatId}
          handleSubmit={handleSubmit}
          setCurrentMessage={setCurrentMessage}
        />
      ) : (
        <Box
          sx={{ margin: "auto", width: "100%", overflowY: "auto" }}
          ref={messagesContainerRef}
          onScroll={() => {
            const el = messagesContainerRef.current;
            if (!el) return;
            const prevScrollTop = prevScrollTopRef.current;
            const currentScrollTop = el.scrollTop;
            const maxScrollTop = el.scrollHeight - el.clientHeight;
            // If user scrolls up (scrollTop decreases), disable auto-scroll
            if (currentScrollTop < prevScrollTop) {
              setAutoScrollEnabled(false);
            }
            // If user scrolls to the bottom, re-enable auto-scroll
            if (currentScrollTop >= maxScrollTop) {
              setAutoScrollEnabled(true);
            }
            prevScrollTopRef.current = currentScrollTop;
          }}
        >
          {messages.map((message, index) => {
            // For the last message, use streaming response if available
            // For bot messages, use the most recent content
            const messageContent =
              index === messages.length - 1 && !message.fromUser
                ? {
                    ...message,
                    message:
                      currentStreamingContent.current || message.message || "",
                  }
                : message;

            // Determine if this is the last bot message and sources are loading
            const isLastBotMessage =
              index === messages.length - 1 && !message.fromUser;
            const shouldShowSourcesLoading =
              isLastBotMessage &&
              isLoadingSources &&
              isAnswerComplete;

            // Create a stable key that includes timestamp for streaming updates
            const key = `${index}-${messageContent.exchangeId || Date.now()}-${
              isLoading.current ? "streaming" : "complete"
            }`;
            return (
              <ChatBubble
                key={key}
                message={messageContent}
                index={index}
                previousMessage={messages[index - 1]}
                chatId={chatId}
                isLoadingSources={shouldShowSourcesLoading}
              />
            );
          })}
          <div ref={endOfMessages} />

          {/** if the bot's answer is still loading... */}
          {isLoading.current && <AnswerLoadingState />}
        </Box>
      )}
      <InputArea
        currentMessage={currentMessage}
        disabled={isLoading.current || isLoadingSources}
        handleSubmit={handleSubmit}
        handleInputChange={handleInputChange}
        handleChatFilterClick={handleChatFilterExpandedChange}
        isWebToolEnabled={isWebToolEnabled}
        handleWebToolToggle={handleWebToolToggle}
      />
      <ChatFilters
        isExpanded={isChatFilterExpanded}
        onIsExpandedChange={handleChatFilterExpandedChange}
      />
    </Box>
  );
};
